import { useState, useCallback, useEffect, useRef } from 'react';
import { listFoldersByTenant } from '../../../../../../services/folders';
import { listDocumentsByTenant } from '../../../../../../services/document';

// Helper function to get file type from document name
const getFileType = (fileName) => {
  if (!fileName) return 'default';
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension || 'default';
};

/**
 * Hook to manage folder tree structure with lazy loading
 * Uses plain objects instead of Map/Set for React compatibility
 * @param {string} vaultUuid - The vault UUID to load data from
 * @param {boolean} isOpen - Whether the modal is open
 * @returns {Object} Tree state and actions
 */
export const useFolderTree = (vaultUuid, isOpen) => {
  // Tree structure: { [folderUuid | 'root']: { folders: [], documents: [] } }
  const [treeData, setTreeData] = useState({});
  // Loading folders: { [folderUuid]: true }
  const [loadingFolders, setLoadingFolders] = useState({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  
  // Track to prevent duplicate loads
  const loadingRef = useRef({});
  const loadedRef = useRef({});
  const lastVaultUuidRef = useRef(null);
  
  // Load root folders and documents when vault is available
  useEffect(() => {
    const loadRootData = async () => {
      // If modal is closed, reset loading state
      if (!isOpen) {
        setIsInitialLoading(false);
        return;
      }
      
      // If modal is open but vaultUuid is not available yet, keep loading
      // (drives might still be loading)
      if (!vaultUuid) {
        setIsInitialLoading(true);
        return;
      }
      
      // Skip if already loaded for this vault
      if (lastVaultUuidRef.current === vaultUuid && loadedRef.current['root']) {
        setIsInitialLoading(false);
        return;
      }
      
      // Reset if vault changed
      if (lastVaultUuidRef.current !== vaultUuid) {
        setTreeData({});
        setLoadingFolders({});
        loadingRef.current = {};
        loadedRef.current = {};
        lastVaultUuidRef.current = vaultUuid;
      }
      
      // Check if already loading
      if (loadingRef.current['root']) {
        return;
      }
      
      loadingRef.current['root'] = true;
      setIsInitialLoading(true);
      
      try {
        const [foldersResponse, documentsResponse] = await Promise.all([
          listFoldersByTenant({
            vault_uuid: vaultUuid,
            parent_folder_uuid: null,
            page: 1,
            page_size: 100,
            order_by: ["updated_at"],
            order_direction: ["DESC"],
            filters: {},
            cursor: null
          }),
          listDocumentsByTenant({
            vault_uuid: vaultUuid,
            page: 1,
            page_size: 100
            // No folder_uuid = root documents only
          })
        ]);
        
        const folders = foldersResponse?.data?.body || [];
        const documents = documentsResponse?.data?.body || [];
        
        // Transform to tree structure - check all possible ID fields
        // Filter out self-referencing folders
        const rootFolders = folders
          .filter(folder => {
            const folderId = folder.folder_uuid || folder.folderUuid || folder.id || folder.uuid;
            const parentId = folder.parent_folder_uuid || folder.parentFolderUuid;
            // Exclude self-references
            return parentId !== folderId;
          })
          .map(folder => {
            const folderId = folder.folder_uuid || folder.folderUuid || folder.id || folder.uuid;
            return {
              id: folderId,
              name: folder.name,
              type: 'folder',
              folder_uuid: folderId
            };
          });
        
        const rootDocuments = documents.map(doc => {
          const docId = doc.document_uuid || doc.documentUuid || doc.id || doc.uuid;
          return {
            id: docId,
            name: doc.name,
            type: getFileType(doc.name),
            document_uuid: docId
          };
        });
        
        setTreeData(prev => ({
          ...prev,
          root: { folders: rootFolders, documents: rootDocuments }
        }));
        
        loadedRef.current['root'] = true;
      } catch (error) {
        // Error loading root data
      } finally {
        delete loadingRef.current['root'];
        setIsInitialLoading(false);
      }
    };
    
    loadRootData();
  }, [vaultUuid, isOpen]);
  
  // Load folder children when expanded
  const loadFolderChildren = useCallback(async (folderUuid) => {
    if (!vaultUuid || !folderUuid) {
      return;
    }
    
    // Check if already loaded
    if (loadedRef.current[folderUuid]) {
      return;
    }
    
    // Check if already loading
    if (loadingRef.current[folderUuid]) {
      return;
    }
    
    loadingRef.current[folderUuid] = true;
    setLoadingFolders(prev => ({ ...prev, [folderUuid]: true }));
    
    try {
      const [foldersResponse, documentsResponse] = await Promise.all([
        listFoldersByTenant({
          parent_folder_uuid: folderUuid, // Use parent_folder_uuid to get subfolders
          vault_uuid: vaultUuid,
          page: 1,
          page_size: 100,
          order_by: ["updated_at"],
          order_direction: ["DESC"],
          filters: {},
          cursor: null
        }),
        listDocumentsByTenant({
          vault_uuid: vaultUuid,
          folder_uuid: folderUuid, // Use folder_uuid to get documents in this folder
          page: 1,
          page_size: 100
        })
      ]);
      
      const folders = foldersResponse?.data?.body || [];
      const documents = documentsResponse?.data?.body || [];
      
      // Transform to tree structure - check all possible ID fields
      // Filter out self-referencing folders and the parent folder itself
      const childFolders = folders
        .filter(folder => {
          const folderId = folder.folder_uuid || folder.folderUuid || folder.id || folder.uuid;
          const parentId = folder.parent_folder_uuid || folder.parentFolderUuid;
          // Exclude the folder we're viewing and self-references
          if (folderId === folderUuid) return false;
          if (parentId === folderId) return false;
          return true;
        })
        .map(folder => {
          const folderId = folder.folder_uuid || folder.folderUuid || folder.id || folder.uuid;
          return {
            id: folderId,
            name: folder.name,
            type: 'folder',
            folder_uuid: folderId
          };
        });
      
      const childDocuments = documents.map(doc => {
        const docId = doc.document_uuid || doc.documentUuid || doc.id || doc.uuid;
        return {
          id: docId,
          name: doc.name,
          type: getFileType(doc.name),
          document_uuid: docId
        };
      });
      
      // Update treeData
      setTreeData(prev => ({
        ...prev,
        [folderUuid]: { folders: childFolders, documents: childDocuments }
      }));
      
      loadedRef.current[folderUuid] = true;
    } catch (error) {
      // Error loading folder children
    } finally {
      delete loadingRef.current[folderUuid];
      // ✅ OPTIMIZED: Use destructuring instead of create + mutate
      setLoadingFolders(prev => {
        const { [folderUuid]: removed, ...rest } = prev;
        return rest;
      });
    }
  }, [vaultUuid]);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTreeData({});
      setLoadingFolders({});
      setIsInitialLoading(false); // Set to false when closed
      loadingRef.current = {};
      loadedRef.current = {};
      lastVaultUuidRef.current = null;
    } else {
      // When modal opens, set loading to true if we don't have vaultUuid yet
      if (!vaultUuid) {
        setIsInitialLoading(true);
      }
    }
  }, [isOpen, vaultUuid]);
  
  return {
    treeData,
    loadingFolders,
    isInitialLoading,
    loadFolderChildren
  };
};
